import {
  Box,
  FormHelperText,
  LinearProgress,
  Typography,
} from '@material-ui/core';
import React from 'react';
import { cognitoSessionToTokenSession } from '~/src/utils/cognitoUtils';
import { SsoAccount, useAwsApi, useLoginApi } from '../../../hooks';
import { Button, Form, Link, TextField } from '../../../components/styled';

/**
 * email: ask for the email and look up its SSO account(s).
 * password: the email is known not to route to SSO; ask for the password.
 */
type Step = 'email' | 'password';

/**
 * /auth/login?accountId=… falls back to this page, keeping accountId in the query, when the
 * account has no SSO provider: that email can only sign in with a password.
 */
function initialStep(): Step {
  return new URLSearchParams(window.location.search).has('accountId')
    ? 'password'
    : 'email';
}

/** The account-pinned login route, carrying the page's own query (destination, appSubdomain). */
function ssoLoginUrl(accountId: string): string {
  const params = new URLSearchParams(window.location.search);
  params.set('accountId', accountId);
  return `/auth/login?${params.toString()}`;
}

export function EmailLogin() {
  const [step, setStep] = React.useState<Step>(initialStep);
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [ssoAccounts, setSsoAccounts] = React.useState<SsoAccount[]>([]);
  const [hasDirectSignIn, setHasDirectSignIn] = React.useState(false);
  const [isLoading, setLoading] = React.useState(false);
  const [promptForMfaCode, setPromptForMfaCode] = React.useState(false);
  const [mfaCode, setMfaCode] = React.useState('');
  const [error, setError] = React.useState('');
  const { loginWithCredentials, sendMfaCode } = useAwsApi();
  const { lookup, setSession } = useLoginApi();

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    setError('');
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
    setError('');
  };

  const handleEmailSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { ssoAccounts: accounts, hasDirectSignIn: direct } =
        await lookup(email);
      // A session created through an IdP can only use that IdP's accounts, so someone who can
      // also sign in directly must choose; everyone else with one SSO account goes straight on.
      if (accounts.length === 1 && !direct) {
        document.location.assign(ssoLoginUrl(accounts[0].accountId));
        return;
      }

      setSsoAccounts(accounts);
      setHasDirectSignIn(Boolean(direct));
      if (accounts.length === 0) setStep('password');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const sendVerification = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);

    try {
      const session = await sendMfaCode(mfaCode);
      const tokenSession = cognitoSessionToTokenSession(session);

      const { redirect } = await setSession({
        session: tokenSession,
      });

      document.location.assign(
        redirect ?? `/account-selector${window.location.search}`,
      );
    } catch (err) {
      setError('MFA Code not valid. Try again.');
      setLoading(false);
    }
  };

  const handleFormSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);

    try {
      // The identifier goes to Cognito exactly as typed: usernames are case-sensitive.
      const { session, totp } = await loginWithCredentials(email, password);
      if (!session) {
        // TODO: add email challenge checker
        if (totp.challengeName === 'SOFTWARE_TOKEN_MFA') {
          setLoading(false);
          setPromptForMfaCode(true);
          return;
        }
        throw new Error('Unrecognized login error');
      }

      const tokenSession = cognitoSessionToTokenSession(session);

      const { redirect } = await setSession({
        session: tokenSession,
      });

      document.location.assign(
        redirect ?? `/account-selector${window.location.search}`,
      );
    } catch (err) {
      setError('Unable to login. Try again or contact support.');
      setLoading(false);
    }
  };

  const wrapHandleFormSubmit = (event: React.FormEvent) => {
    handleFormSubmit(event).catch((err) => {
      setError(err.message);
    });
  };

  const wrapHandleEmailSubmit = (event: React.FormEvent) => {
    handleEmailSubmit(event).catch((err) => {
      setError(err.message);
      setLoading(false);
    });
  };

  const backToEmail = () => {
    setSsoAccounts([]);
    setHasDirectSignIn(false);
    setPassword('');
    setError('');
    setStep('email');
  };

  const renderChooser = () => (
    <Box display="flex" flexDirection="column">
      <Typography variant="subtitle1">
        {hasDirectSignIn
          ? 'Choose how to sign in. Single sign-on only reaches the accounts it manages; use Google or a password to switch between all of your accounts.'
          : 'Your email can sign in to more than one account. Choose one to continue:'}
      </Typography>
      {ssoAccounts.map((account) => (
        <Button
          key={account.accountId}
          type="button"
          onClick={() =>
            document.location.assign(ssoLoginUrl(account.accountId))
          }
        >
          {ssoAccounts.length > 1
            ? (account.accountName ?? account.accountId)
            : 'Continue with single sign-on'}
        </Button>
      ))}
      {hasDirectSignIn && (
        <>
          <Button
            type="button"
            variant="outlined"
            onClick={() =>
              document.location.replace(
                `/auth/google/authorize${window.location.search}`,
              )
            }
          >
            Sign in with Google
          </Button>
          <Button
            type="button"
            variant="outlined"
            onClick={() => {
              setSsoAccounts([]);
              setStep('password');
            }}
          >
            Sign in with a password
          </Button>
        </>
      )}
      <Button type="button" variant="text" onClick={backToEmail}>
        Use a different email
      </Button>
    </Box>
  );

  const renderEmailStep = () => (
    <>
      <Form onSubmit={wrapHandleEmailSubmit} id="login-email">
        <TextField
          type="email"
          label="Email"
          onChange={handleEmailChange}
          value={email}
          autoFocus
        />
        <Button disabled={!email || isLoading}>Next</Button>
      </Form>
      <Button
        type="button"
        variant="text"
        onClick={() => setStep('password')}
        disabled={isLoading}
      >
        Sign in with a password instead
      </Button>
    </>
  );

  const renderPasswordStep = () => (
    <>
      <Form onSubmit={wrapHandleFormSubmit} id="login-sign-in">
        <TextField
          type="email"
          label="Email"
          onChange={handleEmailChange}
          value={email}
          autoFocus={!email}
        />
        <TextField
          label="Password"
          onChange={handlePasswordChange}
          type="password"
          value={password}
          autoFocus={!!email}
        />
        <Button disabled={!email || !password || isLoading}>Sign In</Button>
      </Form>
      <Typography variant="subtitle2">
        No password? Your organization may use single sign-on: go back and enter
        your email, use &quot;Get links to your accounts&quot;, or sign in with
        Google.
      </Typography>
      <Button type="button" variant="text" onClick={backToEmail}>
        Use a different email
      </Button>
    </>
  );

  const renderStep = () => {
    if (ssoAccounts.length > 1 || (ssoAccounts.length === 1 && hasDirectSignIn))
      return renderChooser();
    if (step === 'email') return renderEmailStep();
    return renderPasswordStep();
  };

  return (
    <>
      {isLoading && <LinearProgress />}
      {promptForMfaCode ? (
        <Box display="flex" flexDirection="column">
          <Typography variant="subtitle1">
            Enter your MFA verification code from your Authenticator app:
          </Typography>
          <Form onSubmit={sendVerification} id="login-mfa">
            <TextField
              label="MFA Code"
              onChange={(e) => setMfaCode(e.target.value)}
              value={mfaCode}
            />
            <Button disabled={!mfaCode || isLoading}>Submit</Button>
          </Form>
          <Typography variant="subtitle2">
            Lost access to your Authenticator app? Contact an account
            administrator to reset your MFA.
          </Typography>
          <Button onClick={() => setPromptForMfaCode(false)}>Back</Button>
        </Box>
      ) : (
        renderStep()
      )}
      {error && <FormHelperText error>{error}</FormHelperText>}
      {!promptForMfaCode && (
        <Box display="flex" justifyContent="space-between">
          <Link to="/forgot-password">Forgot Password?</Link>
          <Link to="/sign-up">Sign up</Link>
        </Box>
      )}
    </>
  );
}
